<script setup lang="ts">
import SmallModel from "@/components/Interface/Modal/ModelSizes/SmallModel.vue";
import {useGameStore} from "@/stores/gameStore.ts";
import Backend from '@/models/backend.ts'
import {useShipDesignStore} from "@/stores/shipDesignStore.ts";
import { ref } from 'vue'
import { useFleetStore } from '@/stores/fleetStore.ts'
import ActionButton from '@/components/Elements/Buttons/ActionButton.vue'
import GenericIconWithNumber from '@/components/Elements/Images/Icons/GenericIconWithNumber.vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

// Get the planet data
const gameStore = useGameStore()
const shipDesignStore = useShipDesignStore()
const fleetStore = useFleetStore()

const backend = new Backend()

const targetFleetId = ref(0)
const queuePosition = ref('end_of_queue')


// Handle form submission
const setProductionObject = (designId:number, count:number) => {
  backend.setPlanetProductionObjectShip(gameStore.modalData.planetIds, designId, count, targetFleetId.value, queuePosition.value);
  if(gameStore.modalData.planetIds.length > 1) {
    gameStore.setTinySuccessToastMessage(t('modals.buildShip.addedToQueueMultiple'));
  } else {
    gameStore.setTinySuccessToastMessage(t('modals.buildShip.addedToQueue'));
  }
}

</script>

<template>
  <SmallModel :title="t('modals.buildShip.title')">
    <div class="">
      <div style="max-height: 25rem;" class="overflow-y-scroll">
        <table class="w-full" >
          <tbody>
          <template v-for="design in [...shipDesignStore.playerShipDesigns.values()].sort((a, b) => b.id - a.id)" :key="design.id">
          <tr v-if="!design.deleted"
                class="border-b border-b-slate-700">
              <td class="px-2 py-3 text-slate-300 text-left">
                {{ design.name }}
              </td>
              <td class="px-2 py-3 flex justify-end items-center">
                <ActionButton @click="setProductionObject(design.id,1)">
                  <GenericIconWithNumber :amount="design.productionCost" type="production" text-color="text-slate-300"/>
                </ActionButton>
                <div class="pr-2"></div>
                <ActionButton @click="setProductionObject(design.id,10)">x10</ActionButton>
              </td>
            </tr>
          </template>
          </tbody>
        </table>
      </div>
      <div class="bg-slate-700 text-slate-300 p-2">{{ t('modals.buildShip.options') }}</div>
      <div class="pt-2 text-subtext">
        <div class="pb-2 flex justify-center">
          <div class="flex items-center">{{ t('modals.buildShip.assignTo') }}</div>
          <div class="ps-2">
            <select v-model="targetFleetId" class="pointer-events-auto p-2 text-sm border rounded bg-gray-700 border-gray-600 placeholder-gray-400 text-white focus:ring-blue-500 focus:border-blue-500">
              <option value="0">{{ t('modals.buildShip.noFleet') }}</option>
              <template v-for="fleet in [...fleetStore.fleets.values()].filter(fleet => fleet && typeof fleet.name === 'string' && fleet.name.trim()).sort((a, b) => a.name.localeCompare(b.name))" :key="fleet.id">
                <option :value="fleet.id">{{ fleet.name }}</option>
              </template>
            </select>
          </div>
        </div>
      </div>
      <div class="flex justify-center pb-2 text-subtext">
        <div class="flex items-center pr-2">{{ t('modals.buildShip.queuePosition') }}</div>
        <div class="ps-2">
          <select
            v-model="queuePosition"
            class="pointer-events-auto p-2 text-sm border rounded bg-gray-700 border-gray-600 placeholder-gray-400 text-white focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="after_current">{{ t('modals.buildBuilding.queueOptions.afterCurrent') }}</option>
            <option value="end_of_queue" selected>{{ t('modals.buildBuilding.queueOptions.endOfQueue') }}</option>
            <option value="replace_clear">{{ t('modals.buildBuilding.queueOptions.replaceClear') }}</option>
            <option value="replace_keep">{{ t('modals.buildBuilding.queueOptions.replaceKeep') }}</option>
          </select>
        </div>
      </div>
    </div>
  </SmallModel>
</template>


<style scoped lang="scss">
select {
  @apply bg-slate-600 p-1 text-slate-400 focus:outline-none
}
</style>